import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { SbHost } from 'src/app/legacy/model/sb-host-enum';
import { SbPath } from 'src/app/legacy/model/sb-path';
import { share } from 'rxjs/operators';
import { AppConfigQuery } from '../../core/state/app-config/app-config.query';
import { SbWidgetContext } from '@models';
import { Cacheable } from 'ts-cacheable';
@Injectable({
    providedIn: 'root'
})
export class SbDataService {
    widgetDataBaseUrl: string;
    constructor(private http: HttpClient, private config: AppConfigQuery) {}

    public getData(urlPath: string): Observable<any> {
        return this.http.get(urlPath).pipe(share());
    }

    /**
     * This firstly determines if the data request can be cached based on the widget ID provided in `contextWidgetId`.
     * Then, if the widget ID indicates that caching is safe (according to the `isCachingSafe` method),
     * a cacheable HTTP request is made. Otherwise, a standard HTTP request is executed.
     * SWIM-5221
     * @param path
     * @param params
     * @param defaultBasePath
     * @param contextWidgetId (Optional) context.widgetId
     * @returns An Observable from the cache or server.
     */
    public getDataWithParams(
        path: SbPath,
        params: HttpParams,
        defaultBasePath: SbHost,
        contextWidgetId?: string
    ): Observable<any> {
        if (contextWidgetId && this.isCachingSafe(contextWidgetId)) {
            return this.httpGetCacheableDataWithParams(path, params, defaultBasePath);
        }
        return this.httpGetDataWithParams(path, params, defaultBasePath);
    }

    @Cacheable({ maxAge: 1000 })
    public httpGetCacheableDataWithParams(
        path: SbPath,
        params: HttpParams,
        defaultBasePath: SbHost
    ): Observable<any> {
        return this.httpGetDataWithParams(path, params, defaultBasePath);
    }

    public httpGetDataWithParams(
        path: SbPath,
        params: HttpParams,
        defaultBasePath: SbHost
    ): Observable<any> {
        // To join the values into a comma-separated string and set it back in the new HttpParams object - SWIM-4658
        let newParams = new HttpParams();
        for (const key of params.keys()) {
            const values = params.getAll(key);
            if (values) {
                newParams = newParams.set(key, values.join(','));
            }
        }

        const resourceUrl = this.getResourceUrl(path, defaultBasePath);
        return this.http.get(resourceUrl, { params: newParams }).pipe(share());
    }

    public getResourceUrl(path: SbPath, defaultBasePath?: SbHost) {
        const resource =
            path.resource === '' || path.resource.startsWith('/')
                ? path.resource
                : '/' + path.resource;
        return this.config.getBaseUrl(path, defaultBasePath) + resource;
    }

    public getResource(
        path: SbPath,
        defaultBasePath: SbHost,
        id: string
    ): Observable<any> {
        const resourceUrl = this.getSingleResourceUrl(path, defaultBasePath, id);
        return this.http.get(resourceUrl).pipe(share());
    }

    public getSingleResourceUrl(
        path: SbPath,
        defaultBasePath: SbHost,
        id: string
    ): string {
        const resourceUrl = this.getResourceUrl(path, defaultBasePath);
        return `${resourceUrl}/${id}`;
    }

    /**
     * Identifies if a widget can safely cache data fetched by the getDataWithParams function.
     * @param contextWidgetId context.widgetId
     * @private
     */
    private isCachingSafe(contextWidgetId: string): boolean {
        const safePrefixes = ['portfolio-report', 'positions-report'];
        return safePrefixes.some((prefix) => contextWidgetId.startsWith(prefix));
    }
}
